import type {} from '@mui/material/themeCssVarsAugmentation'
import { ThemeOptions } from '@mui/material'

import { COLOR_CONFIG } from '../constants/config/CONFIG'

import themeComponents from './themeComponents'


interface PaletteColorOptions {
  shadowColor?: string;
}

declare module '@mui/material/styles' {

  interface ZIndex {
    bottomBar: number
    resizeHandle: number
    itemControls: number
    editorDropdown: number
    editorPopover: number
  }

  interface Palette {
    publish: Palette['primary'];
    share: Palette['primary'];
    danger: Palette['primary'];
    scrollbar: Palette['primary'];
  }

  interface PaletteOptions {
    publish?: PaletteOptions['primary'] // Color reserved for any action that affects visibility to public
    share?: PaletteOptions['primary'] // Color reserved for any action that affects visibility to shared
    danger?: PaletteOptions['primary'] // Color for any dangerous action (should mostly be for non-users)
    scrollbar?: PaletteColorOptions
  }

  interface PaletteColor {
    shadowColor?: string;
  }

}

// Components
declare module '@mui/material/Fab' {
  interface FabPropsColorOverrides {
    publish: true
  }
}
declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    private: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    publish: true
    share: true
    danger: true
  }
  interface ButtonPropsVariantOverrides {
    dashed: true
  }
}
declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    namespace: true
    source: true
  }
}
declare module '@mui/material/Icon' {
  interface IconPropsColorOverrides {
    publish: true
    share: true
    danger: true
  }
}
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    publish: true
    share: true
    danger: true
  }
}


export const PUBLIC_COLOR = '#ffd700' // Is used on things that are still private, and can be published (private/shared)
export const SHARE_COLOR = '#388e3c' // Is used on things that are still private, and can be shared (private)

export type ThemeTypes = 'dark' | 'light'
export type ThemeModes = 'dark' | 'light' | 'system' | undefined

export const zIndexes = {
  bottomBar: 40, // So platejs popover is over it at 50

  mobileStepper: 1000,
  resizeHandle: 1001,
  itemControls: 1010,

  fab: 1050,
  speedDial: 1050,

  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,

  editorPopover: 1600,
  editorDropdown: 1700,

  tooltip: 1800,
}

export const getThemeConfig = (): ThemeOptions => {
  const components = themeComponents()

  return {
    typography: {
      fontFamily: [
        'Public Sans',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'sans-serif',
      ].join(','),
    },
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: COLOR_CONFIG.primaryColor,
            // contrastText: colors.grey[50],
          },
          secondary: {
            main: COLOR_CONFIG.secondaryColor,
          },
          success: {
            main: COLOR_CONFIG.successColor,
          },
          info: {
            main: COLOR_CONFIG.infoColor,
          },
          warning: {
            main: COLOR_CONFIG.warningColor,
          },
          error: {
            main: COLOR_CONFIG.errorColor,
          },
          danger: {
            main: '#F01',
          },
          publish: {
            main: PUBLIC_COLOR,
          },
          share: {
            main: SHARE_COLOR,
          },
          scrollbar: {
            shadowColor: 'rgba(0, 0, 0, 0.3)',
          },
        }
      },
      dark: {
        palette: {
          primary: {
            main: COLOR_CONFIG.primaryColorDark,
            //   contrastText: colors.grey[50],
          },
          secondary: {
            main: COLOR_CONFIG.secondaryColorDark,
          },
          success: {
            main: COLOR_CONFIG.successColorDark,
          },
          info: {
            main: COLOR_CONFIG.infoColorDark,
          },
          warning: {
            main: COLOR_CONFIG.warningColorDark,
          },
          error: {
            main: COLOR_CONFIG.errorColorDark,
          },
          danger: {
            main: '#F01',
          },
          publish: {
            main: PUBLIC_COLOR,
          },
          share: {
            main: SHARE_COLOR,
          },
          scrollbar: {
            shadowColor: 'rgba(255, 255, 255, 0.3)',
          },
        }
      },
    },
    breakpoints: {
      values: {
        xs: 400,
        sm: 700,
        md: 900,
        lg: 1300,
        xl: 1536,
      },
    },
    zIndex: zIndexes,
    components,
  }
}
